import { purgeLegacyFinanceCaches } from '../core/storage/purgeLegacyFinanceCaches';
import type {
  AnalyticsDailyResponse,
  AnalyticsPeriod,
  AnalyticsSummaryResponse,
  AnalyticsTrendsResponse,
  CardSpendResponse,
  SpendSummaryResponse,
} from '../types/analytics';
import { apiRequest } from './apiClient';
import { notifyDashboardRefresh } from './dashboardRefreshBus';

export type GetAnalyticsSummaryOptions = {
  accessToken: string;
  userId: string;
  period?: AnalyticsPeriod;
  /** Explicit period window (e.g. `2026-07`). Defaults to current period on the server. */
  periodKey?: string;
  /** Mine vs Household combined view. */
  view?: 'personal' | 'combined';
  /** After import / pull-to-refresh — skip painting stale cache; bust query cache. */
  forceRefresh?: boolean;
};

function bustedQuery(params: URLSearchParams): string {
  params.set('_', String(Date.now()));
  return params.toString();
}

async function fetchAnalyticsSummaryNetwork(
  accessToken: string,
  period: AnalyticsPeriod,
  periodKey?: string,
  view?: 'personal' | 'combined',
): Promise<AnalyticsSummaryResponse> {
  const params = new URLSearchParams({ period });
  if (periodKey) params.set('periodKey', periodKey);
  if (view) params.set('view', view);
  return apiRequest<AnalyticsSummaryResponse>(`/analytics/summary?${bustedQuery(params)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

/**
 * Always hits the network. Spend totals are never stored on device.
 */
export async function getAnalyticsSummary(
  options: GetAnalyticsSummaryOptions,
): Promise<AnalyticsSummaryResponse> {
  const period = options.period ?? 'month';
  const { accessToken, periodKey, view } = options;
  return fetchAnalyticsSummaryNetwork(accessToken, period, periodKey, view);
}

/**
 * Network only.
 */
export async function loadAnalyticsSummary(
  options: GetAnalyticsSummaryOptions,
  onUpdate: (data: AnalyticsSummaryResponse, source: 'cache' | 'network') => void,
): Promise<void> {
  const period = options.period ?? 'month';
  const { accessToken, periodKey, view } = options;
  const fresh = await fetchAnalyticsSummaryNetwork(accessToken, period, periodKey, view);
  onUpdate(fresh, 'network');
}

/** Direct network fetch. */
export async function fetchAnalyticsSummary(
  accessToken: string,
  period: AnalyticsPeriod = 'month',
  periodKey?: string,
): Promise<AnalyticsSummaryResponse> {
  return fetchAnalyticsSummaryNetwork(accessToken, period, periodKey);
}

export async function invalidateAnalyticsCache(_userId?: string): Promise<void> {
  await purgeLegacyFinanceCaches();
}

/**
 * Wake Home / Spend after import (no local snapshot to wipe).
 */
export async function forcePullDashboards(options?: {
  userId?: string;
  reason?: string;
}): Promise<void> {
  await purgeLegacyFinanceCaches();
  notifyDashboardRefresh(options?.reason ?? 'force_pull');
}

/** Spend-tab category totals — always network (no local summary cache). */
export async function getAnalyticsSpend(options: {
  accessToken: string;
  period?: AnalyticsPeriod;
  periodKey?: string;
  /** Mine vs Household combined view. */
  view?: 'personal' | 'combined';
  spendAccountId?: string;
  /** Cache-bust query so intermediaries can't serve a stale spend snapshot. */
  forceRefresh?: boolean;
}): Promise<SpendSummaryResponse> {
  const period = options.period ?? 'month';
  const params = new URLSearchParams({ period });
  if (options.periodKey) params.set('periodKey', options.periodKey);
  if (options.view) params.set('view', options.view);
  if (options.spendAccountId) params.set('spendAccountId', options.spendAccountId);
  return apiRequest<SpendSummaryResponse>(`/analytics/spend?${bustedQuery(params)}`, {
    headers: { Authorization: `Bearer ${options.accessToken}` },
  });
}

/** Credit-card spend + statement payment totals for the Spend cards widget. */
export async function getAnalyticsCards(options: {
  accessToken: string;
  period?: AnalyticsPeriod;
  periodKey?: string;
  view?: 'personal' | 'combined';
  forceRefresh?: boolean;
}): Promise<CardSpendResponse> {
  const period = options.period ?? 'month';
  const params = new URLSearchParams({ period });
  if (options.periodKey) params.set('periodKey', options.periodKey);
  if (options.view) params.set('view', options.view);
  return apiRequest<CardSpendResponse>(`/analytics/cards?${bustedQuery(params)}`, {
    headers: { Authorization: `Bearer ${options.accessToken}` },
  });
}

/** Month (or week/year) series for Spend charts — supports optional category filter. */
export async function getAnalyticsTrends(options: {
  accessToken: string;
  period?: AnalyticsPeriod;
  limit?: number;
  categorySlug?: string;
  tab?: 'spend' | 'wealth';
  view?: 'personal' | 'combined';
  forceRefresh?: boolean;
}): Promise<AnalyticsTrendsResponse> {
  const params = new URLSearchParams({
    period: options.period ?? 'month',
    tab: options.tab ?? 'spend',
    limit: String(options.limit ?? 6),
  });
  if (options.categorySlug) params.set('categorySlug', options.categorySlug);
  if (options.view) params.set('view', options.view);
  return apiRequest<AnalyticsTrendsResponse>(`/analytics/trends?${bustedQuery(params)}`, {
    headers: { Authorization: `Bearer ${options.accessToken}` },
  });
}

/** Per-day spend totals for a calendar-year heatmap. */
export async function getAnalyticsDaily(options: {
  accessToken: string;
  year: number;
  tab?: 'spend' | 'wealth';
  categorySlug?: string;
  view?: 'personal' | 'combined';
  forceRefresh?: boolean;
}): Promise<AnalyticsDailyResponse> {
  const params = new URLSearchParams({
    year: String(options.year),
    tab: options.tab ?? 'spend',
  });
  if (options.categorySlug) params.set('categorySlug', options.categorySlug);
  if (options.view) params.set('view', options.view);
  return apiRequest<AnalyticsDailyResponse>(`/analytics/daily?${bustedQuery(params)}`, {
    headers: { Authorization: `Bearer ${options.accessToken}` },
  });
}
